import {
  listNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  clearAllNotifications,
} from "@/services/notificationService";
import { getEcho, initEcho, refreshEchoAuthToken } from "@/plugins/echo";

const state = () => ({
  items: [],
  unreadCount: 0,
  loaded: false,
  loading: false,
  subscribedUserId: null,
});

const getters = {
  items: (s) => s.items,
  unreadItems: (s) => s.items.filter((n) => !n.is_read),
  unreadCount: (s) => s.unreadCount,
  loaded: (s) => s.loaded,
};

const mutations = {
  SET_LOADING(state, v) {
    state.loading = v;
  },
  SET_ITEMS(state, items) {
    state.items = items;
    state.loaded = true;
  },
  SET_UNREAD_COUNT(state, n) {
    state.unreadCount = n;
  },
  UPSERT(state, notification) {
    const idx = state.items.findIndex((n) => n.id === notification.id);
    if (idx === -1) {
      state.items.unshift(notification);
      if (!notification.is_read) state.unreadCount += 1;
    } else {
      const wasUnread = !state.items[idx].is_read;
      state.items.splice(idx, 1, notification);
      if (wasUnread && notification.is_read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      } else if (!wasUnread && !notification.is_read) {
        state.unreadCount += 1;
      }
    }
  },
  MARK_READ(state, id) {
    const n = state.items.find((x) => x.id === id);
    if (n && !n.is_read) {
      n.is_read = true;
      n.read_at = new Date().toISOString();
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    }
  },
  MARK_ALL_READ(state) {
    state.items.forEach((n) => {
      if (!n.is_read) {
        n.is_read = true;
        n.read_at = new Date().toISOString();
      }
    });
    state.unreadCount = 0;
  },
  REMOVE(state, id) {
    const idx = state.items.findIndex((n) => n.id === id);
    if (idx === -1) return;
    const [removed] = state.items.splice(idx, 1);
    if (removed && !removed.is_read) {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    }
  },
  CLEAR_ALL(state) {
    state.items = [];
    state.unreadCount = 0;
  },
  SET_SUBSCRIBED_USER(state, id) {
    state.subscribedUserId = id;
  },
  RESET(state) {
    state.items = [];
    state.unreadCount = 0;
    state.loaded = false;
    state.loading = false;
    state.subscribedUserId = null;
  },
};

const actions = {
  async fetch({ commit, state, dispatch }) {
    if (state.loading) return;
    commit("SET_LOADING", true);
    try {
      const res = await listNotifications();
      commit("SET_ITEMS", res.data || []);
      commit("SET_UNREAD_COUNT", res.meta?.unread_count || 0);

      // salvia-779: quem abre o Salvia DEPOIS do aviso "consulta começando"
      // (estava fora da página quando ele chegou) ainda ganha o popup, se a
      // consulta continua na janela útil. Ver atendimento/alertarPendente.
      dispatch("atendimento/alertarPendente", res.data || [], { root: true });
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async markAsRead({ commit }, id) {
    commit("MARK_READ", id);
    try {
      await markNotificationAsRead(id);
    } catch (err) {
      // se falhar, recarrega pra sincronizar estado
      throw err;
    }
  },

  async markAllAsRead({ commit }) {
    commit("MARK_ALL_READ");
    await markAllNotificationsAsRead();
  },

  async remove({ commit, dispatch }, id) {
    commit("REMOVE", id);
    try {
      await deleteNotification(id);
    } catch (err) {
      // ressincroniza se o backend recusar
      dispatch("fetch").catch(() => {});
      throw err;
    }
  },

  async clearAll({ commit, dispatch }) {
    commit("CLEAR_ALL");
    try {
      await clearAllNotifications();
    } catch (err) {
      dispatch("fetch").catch(() => {});
      throw err;
    }
  },

  subscribe({ commit, state, dispatch }, userId) {
    if (!userId) return;
    if (state.subscribedUserId === userId) return;

    // Marca antes de tentar Echo para evitar re-tentativas em loop.
    commit("SET_SUBSCRIBED_USER", userId);

    // Sempre busca o snapshot via HTTP — independe de o Echo estar saudável.
    dispatch("fetch").catch(() => {});

    try {
      refreshEchoAuthToken();
      const echo = initEcho();

      const channel = echo.private(`App.Models.User.${userId}`);

      channel.listen(".NotificationCreated", (payload) => {
        const notification = payload?.notification || payload;
        commit("UPSERT", notification);

        // salvia-779: "a consulta com fulano vai começar" vira popup com
        // atalho, além de ficar no sino. Esta é a chegada AO VIVO; quem abre
        // a página depois é coberto pelo `fetch` (alertarPendente).
        if (notification?.type === "appointment.starting") {
          dispatch("atendimento/alertarInicio", notification, { root: true });
        }
      });

      channel.listen(".NotificationUpdated", (payload) => {
        const notification = payload?.notification || payload;
        commit("UPSERT", notification);

        // salvia-779: lida em outra aba/aparelho → o popup daqui fecha junto.
        if (notification?.is_read) {
          dispatch("atendimento/notificacaoLida", notification.id, { root: true });
        }
      });
    } catch (e) {
      console.warn("[notifications] subscribe Echo falhou (ignorado):", e);
    }
  },

  unsubscribe({ commit, state }) {
    try {
      const echo = getEcho();
      if (echo && state.subscribedUserId) {
        echo.leave(`App.Models.User.${state.subscribedUserId}`);
      }
    } catch (e) {
      console.warn("[notifications] unsubscribe Echo falhou (ignorado):", e);
    }
    commit("RESET");
  },
};

export default { namespaced: true, state, getters, mutations, actions };
